
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/auth/AuthContext';
import { networkService } from '@/services/network.service';
import { initialSyncService, InitialSyncStatus } from '@/services/sync/InitialSyncService';
import OfflineLogger from '@/utils/offlineLogger';
import { syncQueueService, syncValidationService } from '@/services/sync';
import logger from '@/utils/logger';
import { queryClient } from '@/services/queryClient';

export interface UseInitialSyncResult {
  syncStatus: InitialSyncStatus | null;
  isSyncing: boolean;
  lastSyncTime: number | null;
  triggerSync: () => Promise<void>;
  validateData: () => Promise<{
    isValid: boolean;
    missingEntities: string[];
    details: Record<string, number>;
  }>;
}

// ✅ FIX: Aggiungiamo un lock globale per prevenire sync simultanei
const syncLocks = new Map<string, Promise<void>>();

export const useInitialSync = (): UseInitialSyncResult => {
  const { user, authInitialized } = useAuth();
  const [syncStatus, setSyncStatus] = useState<InitialSyncStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  
  // ✅ FIX: Ref per tracking stabile dello stato
  const userIdRef = useRef<string | null>(null);
  const hasSetupListenersRef = useRef(false);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const logoutRequestedRef = useRef(false);

  const clearAllTimeouts = () => {
    timeoutIdsRef.current.forEach(id => clearTimeout(id));
    timeoutIdsRef.current = [];
  };

  // ✅ Monitor queue status per UI più reattiva
  const [queueStatus, setQueueStatus] = useState(syncQueueService.getQueueStatus());

  // ✅ OTTIMIZZATO: Logging ridotto per evitare spam - solo quando cambia l'utente
  useEffect(() => {
    if (!authInitialized || !user?.id || userIdRef.current === user.id) {
      return;
    }

    // Cleanup precedente prima di iniziare con il nuovo utente
    clearAllTimeouts();
    syncQueueService.cancelAll();
    setQueueStatus(syncQueueService.getQueueStatus());
    logoutRequestedRef.current = false;

    userIdRef.current = user.id;
    hasSetupListenersRef.current = false; // Reset quando cambia utente
    logger.log('useInitialSync', '🔧 Hook initialized for new user', {
      userId: user.id.substring(0, 8),
      authInitialized
    });
  }, [user?.id, authInitialized]);

  // Carica l'ultimo stato di sync dal localStorage
  useEffect(() => {
    if (!authInitialized || !user?.id || userIdRef.current !== user.id) {
      return;
    }

    initialSyncService.getLastSyncStatus(user.id).then(status => {
      if (status) {
        setSyncStatus(status);
        setLastSyncTime(status.timestamp);
        logger.log('useInitialSync', '✅ Last sync status loaded', {
          isComplete: status.isComplete,
          timestamp: new Date(status.timestamp).toISOString()
        });
      }
    });
  }, [user?.id, authInitialized]);

  // 🔁 Ripristina i task di download rimasti in sospeso da una sessione
  // precedente (es. app chiusa durante il delay di un retry): senza questo,
  // i retry persistiti non riprenderebbero mai. Vedi issue #1423.
  useEffect(() => {
    if (!authInitialized || !user?.id || !networkService.isOnline()) {
      return;
    }

    const restored = syncQueueService.restorePersistedTasks(user.id);
    if (restored > 0) {
      logger.log('useInitialSync', `🔁 Ripristinati ${restored} task sync da sessione precedente`);
      setQueueStatus(syncQueueService.getQueueStatus());
    }
  }, [user?.id, authInitialized]);

  // ✅ OTTIMIZZATO: Monitor sync queue status con intervallo ridotto
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const interval = setInterval(() => {
      const status = syncQueueService.getQueueStatus();
      setQueueStatus(status);
      setIsSyncing(status.isProcessing || initialSyncService.isSyncInProgress());
    }, 2000);

    return () => clearInterval(interval);
  }, [user?.id]);

  // 🔄 Resetta lo stato quando l'utente si disconnette
  useEffect(() => {
    if (user?.id) {
      return;
    }

    setIsSyncing(false);
    setSyncStatus(null);
    logoutRequestedRef.current = true;
    syncQueueService.cancelAll();
    setQueueStatus(syncQueueService.getQueueStatus());
    clearAllTimeouts();
  }, [user?.id]);

  // ✅ FIX: Definiamo performSyncSafe come funzione normale
  const performSyncSafe = async (userId: string) => {
    try {
      logger.log('useInitialSync', '🚀🚀🚀 Starting protected sync with optimization');
      logger.log('useInitialSync', '📤 Invoking performInitialSync', {
        userId: userId.substring(0, 8),
        stack: new Error().stack
      });

      const result = await initialSyncService.performInitialSync(userId);

      setSyncStatus(result);
      setLastSyncTime(result.timestamp);

      await queryClient.invalidateQueries({ queryKey: ['shopping-items'] });
      await queryClient.invalidateQueries({ queryKey: ['shopping-list'] });

      if (result.isComplete) {
        logger.log('useInitialSync', '✅✅✅ Protected sync completed successfully', {
          totalEntities: result.totalEntities,
          successfulEntities: result.successfulEntities
        });
      } else {
        logger.warn('useInitialSync', 
          `⚠️⚠️⚠️ Protected sync partially failed: ${result.successfulEntities}/${result.totalEntities} entities synced`
        );
      }
    } catch (error) {
      logger.logError('useInitialSync','Error during protected sync',{additionalInfo:error});
    }
  };

  const triggerSync = useCallback(async () => {
    if (!authInitialized || !user?.id || !networkService.isOnline() || logoutRequestedRef.current) {
      logger.warn('useInitialSync.triggerSync', '❌ Cannot sync - conditions not met', {
        hasUser: !!user?.id,
        isOnline: networkService.isOnline(),
        authInitialized,
        logoutRequested: logoutRequestedRef.current
      });
      return;
    }

    // ✅ FIX: Controllo del lock per prevenire sync simultanei
    const lockKey = `sync_${user.id}`;
    if (syncLocks.has(lockKey)) {
      logger.log('useInitialSync.triggerSync', '⏳ Sync already in progress, waiting...');
      await syncLocks.get(lockKey);
      return;
    }

    // ✅ FIX: Controllo aggiuntivo dello stato isSyncing
    if (isSyncing) {
      logger.log('useInitialSync.triggerSync', '⏳ Sync already running, skipping...');
      return;
    }

    setIsSyncing(true);
    
    const syncPromise = performSyncSafe(user.id);
    syncLocks.set(lockKey, syncPromise);
    
    try {
      await syncPromise;
    } finally {
      syncLocks.delete(lockKey);
      setIsSyncing(false);
    }
  }, [user?.id, authInitialized, isSyncing]);

  const validateData = useCallback(async () => {
    if (!user?.id || logoutRequestedRef.current) {
      logger.warn('useInitialSync.validateData', '❌ Cannot validate - conditions not met', {
        hasUser: !!user?.id,
        logoutRequested: logoutRequestedRef.current
      });
      return {
        isValid: false,
        missingEntities: ['no_user'],
        details: {}
      };
    }

    logger.log('useInitialSync.validateData', '📤 Invoking performComprehensiveValidation', {
      userId: user.id.substring(0, 8),
      stack: new Error().stack
    });

    const validation = await syncValidationService.performComprehensiveValidation(user.id);

    return {
      isValid: validation.isValid,
      missingEntities: validation.missingEntities,
      details: validation.details
    };
  }, [user?.id]);

  // ✅ FIX: Gestione eventi di autenticazione ottimizzata e stabilizzata
  useEffect(() => {
    if (!authInitialized || !user?.id || hasSetupListenersRef.current) {
      return;
    }

    hasSetupListenersRef.current = true;
    let hasTriggeredSync = false;

    const handleUserSignedIn = async (event: CustomEvent) => {
      const { userId } = event.detail;
      
      if (userId && user?.id === userId && !hasTriggeredSync) {
        hasTriggeredSync = true;
        logger.log('useInitialSync', `🔥 IMMEDIATE SYNC: User ${userId.substring(0, 8)} signed in`);
        
        // ✅ FIX: Piccolo delay per evitare race condition con altre inizializzazioni
        const timeoutId = setTimeout(async () => {
          try {
            await triggerSync();
          } catch (error) {
            logger.logError("useInitialSync","Failed to trigger sync on sign in",{additionalInfo:error});
          } finally {
            timeoutIdsRef.current = timeoutIdsRef.current.filter(id => id !== timeoutId);
          }
        }, 100);
        timeoutIdsRef.current.push(timeoutId);
      }
    };

    const handleSessionRestored = async (event: CustomEvent) => {
      const { userId } = event.detail;
      
      if (userId && user?.id === userId && !hasTriggeredSync) {
        hasTriggeredSync = true;
        logger.log('useInitialSync', `🔄 CONDITIONAL SYNC: Session restored for ${userId.substring(0, 8)}`);
        
        // Per sessioni esistenti, controlla se è necessario il sync
        const timeoutId = setTimeout(async () => {
          try {
            const lastSync = await initialSyncService.getLastSyncStatus(userId);
            const now = Date.now();
            const SYNC_INTERVAL = 6 * 60 * 60 * 1000; // 6 ore

            const shouldSync = !lastSync ||
                              !lastSync.isComplete ||
                              (now - lastSync.timestamp) > SYNC_INTERVAL;

            if (shouldSync) {
              await triggerSync();
            } else {
              const validation = await validateData();
              if (!validation.isValid) {
                await triggerSync();
              }
            }
          } catch (error) {
            logger.logError("useInitialSync","Failed to handle session restoration",{additionalInfo:error});
          } finally {
            timeoutIdsRef.current = timeoutIdsRef.current.filter(id => id !== timeoutId);
          }
        }, 1500);
        timeoutIdsRef.current.push(timeoutId);
      }
    };

    logger.log('useInitialSync', '👂 Registering auth event listeners for user', {
      userId: user.id.substring(0, 8)
    });
    
    window.addEventListener('userSignedIn', handleUserSignedIn as EventListener);
    window.addEventListener('userSessionRestored', handleSessionRestored as EventListener);

    return () => {
      logger.log('useInitialSync', '🗑️ Removing auth event listeners for user', {
        userId: user?.id?.substring(0, 8)
      });
      window.removeEventListener('userSignedIn', handleUserSignedIn as EventListener);
      window.removeEventListener('userSessionRestored', handleSessionRestored as EventListener);
      hasSetupListenersRef.current = false;
      clearAllTimeouts();
    };
  }, [user?.id, authInitialized, triggerSync, validateData]);

  return {
    syncStatus,
    isSyncing: isSyncing || queueStatus.isProcessing,
    lastSyncTime,
    triggerSync,
    validateData
  };
};
